import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { fetchFromApi, getMediaUrl } from '@utils/api';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';
import { Icon } from '@components/icons';

const StyledCertificatesSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;

  h2 {
    font-size: clamp(24px, 5vw, var(--fz-heading));
  }

  .certs-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    grid-gap: 20px;
    position: relative;
    margin-top: 50px;
    width: 100%;

    @media (max-width: 1080px) {
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    }
  }
`;

const StyledCertificate = styled.li`
  position: relative;
  cursor: default;
  transition: var(--transition);

  @media (prefers-reduced-motion: no-preference) {
    &:hover,
    &:focus-within {
      .cert-inner {
        transform: translateY(-7px);
      }
    }
  }

  .cert-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    display: flex;
    flex-direction: column;
    position: relative;
    height: 100%;
    border-radius: var(--border-radius);
    background-color: var(--light-navy);
    transition: var(--transition);
    overflow: hidden;
    border: 1px solid var(--lightest-navy);
  }

  .cert-img-wrapper {
    width: 100%;
    height: 180px;
    background-color: var(--navy);
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid var(--lightest-navy);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: var(--transition);
    }
    
    .placeholder-icon {
      color: var(--slate);
      opacity: 0.3;
    }
  }

  .cert-content {
    padding: 1.5rem;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .cert-title {
    margin: 0 0 5px;
    color: var(--lightest-slate);
    font-size: var(--fz-lg);
    font-weight: 600;
    line-height: 1.4;
  }

  .cert-issuer {
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    margin-bottom: 10px;
  }

  .cert-date {
    color: var(--slate);
    font-size: var(--fz-xs);
  }

  .cert-footer {
    display: flex;
    align-items: center;
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid var(--lightest-navy);

    a.verify-link {
      display: flex;
      align-items: center;
      gap: 6px;
      color: var(--light-slate);
      font-size: var(--fz-xs);
      
      &:hover {
        color: var(--green);
      }
      
      svg {
        width: 14px;
        height: 14px;
      }
    }
  }
`;

const Certificates = () => {
  const [certs, setCerts] = useState([]);
  const revealTitle = useRef(null);
  const revealCerts = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const loadCerts = async () => {
      const apiCerts = await fetchFromApi('/certificates');
      if (apiCerts && apiCerts.length > 0) {
        setCerts(apiCerts);
      }
    };
    loadCerts();

    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    revealCerts.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  if (certs.length === 0) {
    return null; // Don't show the section if there are no certificates in the DB
  }

  return (
    <StyledCertificatesSection id="certificates">
      <h2 ref={revealTitle} className="numbered-heading">
        Certifications
      </h2>

      <ul className="certs-grid">
        {certs
          .sort((a, b) => a.order - b.order)
          .map((cert, i) => (
            <StyledCertificate 
              key={cert.id} 
              ref={el => (revealCerts.current[i] = el)}
            >
              <div className="cert-inner">
                <div className="cert-img-wrapper">
                  {cert.thumbnail ? (
                    <img 
                      src={getMediaUrl(cert.thumbnail)} 
                      alt={cert.title} 
                      onError={(e) => {
                        // Fallback if local image not found or fails to load
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <div 
                    className="placeholder-icon" 
                    style={{ display: cert.thumbnail ? 'none' : 'block' }}
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="48" 
                      height="48" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="1" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <path d="M12 15l-2 5l2 2l2-2l-2-5z"/>
                      <path d="M12 2a5 5 0 0 0-5 5v3a5 5 0 0 0 10 0V7a5 5 0 0 0-5-5z"/>
                      <path d="M6 12H4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2"/>
                      <path d="M18 12h2a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2"/>
                    </svg>
                  </div>
                </div>

                <div className="cert-content">
                  <div>
                    <h3 className="cert-title">{cert.title}</h3>
                    <p className="cert-issuer">{cert.issuer}</p>
                    <p className="cert-date">Issued: {cert.issue_date}</p>
                  </div>

                  {cert.credential_url && (
                    <div className="cert-footer">
                      <a 
                        href={cert.credential_url} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="verify-link"
                      >
                        <span>Verify Credential</span>
                        <Icon name="External" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </StyledCertificate>
          ))}
      </ul>
    </StyledCertificatesSection>
  );
};

export default Certificates;
